import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import UserProfileForm from '../components/UserProfileForm';
import ProfileNavigation from '../components/ProfileNavigation';
import { 
  IconUser, 
  IconEdit, 
  IconCalendar, 
  IconClock, 
  IconSchool, 
  IconCalendarEvent,
  IconCrown,
  IconHeartHandshake
} from '@tabler/icons-react';
import styles from './PersonalInfoPage.module.css';
import { userService } from '../services/api';
import { User } from '../types/user';

const PersonalInfoPage: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const navigate = useNavigate();

  // Vérifier d'abord si l'utilisateur est connecté
  useEffect(() => {
    async function checkAuthentication() {
      try {
        const userData = await userService.getCurrentUser();
        setAuthenticated(true);
        setUser(userData);
      } catch (err) {
        console.error('Erreur d\'authentification:', err);
        setAuthenticated(false);
        setError('Erreur lors de la récupération des informations utilisateur');
      } finally {
        setLoading(false);
      }
    }
    
    checkAuthentication();
  }, []);

  const toggleProfileForm = () => {
    setShowProfileForm(!showProfileForm);
  };

  // Calculer l'âge à partir de la date de naissance
  const calculateAge = (dateOfBirth: string | undefined) => {
    if (!dateOfBirth) return 'Non renseigné';
    
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  // Formater la date pour l'affichage
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Non renseigné';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Afficher un message si l'utilisateur n'est pas connecté
  if (authenticated === false) {
    navigate('/login');
    return null;
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Mon profil</h1>
      <p className={styles.subtitle}>
        Gérez vos informations personnelles et vos préférences.
      </p>
      
      <ProfileNavigation />
      
      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>Chargement des informations...</p>
        </div>
      ) : error ? (
        <div className={styles.errorContainer}>
          <p className={styles.errorText}>{error}</p>
        </div>
      ) : showProfileForm ? (
        <UserProfileForm isEditing={true} />
      ) : user ? (
        <>
          {/* En-tête du profil simplifié sans avatar */}
          <div className={styles.profileHeader}>
            <h2 className={styles.userName}>{user.firstName} {user.lastName}</h2>
            <p className={styles.userEmail}>{user.email}</p>
            
            {/* Métadonnées utilisateur */}
            <div className={styles.userMeta}>
              {/* Afficher "Événements créés" seulement pour les administrateurs ou les organisations */}
              {user.role !== 'user' && (
                <div className={styles.metaItem}>
                  <span className={styles.metaValue}>{user.eventsCreatedCount || 0}</span>
                  <span className={styles.metaLabel}>Événements créés</span>
                </div>
              )}
              <div className={styles.metaItem}>
                <span className={styles.metaValue}>{user.eventsAttendedCount || 0}</span>
                <span className={styles.metaLabel}>Participations</span>
              </div>
              {/* Le champ Connexions a été retiré pour tous les utilisateurs */}
            </div>
            
            <button 
              className={styles.editButton} 
              onClick={toggleProfileForm}
              aria-label="Modifier les informations du profil"
              style={{ marginTop: '1.5rem' }}
            >
              <IconEdit size={18} />
              Modifier mon profil
            </button>
          </div>
        
          {/* Section des informations personnelles */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <IconUser size={24} />
                Informations personnelles
              </h2>
            </div>
            
            <div className={styles.profileInfo}>
              <div className={styles.profileSection}>
                <h3>Identité</h3>
                <div className={styles.profileRow}>
                  <span className={styles.profileLabel}>Nom complet</span>
                  <span>{user.firstName} {user.lastName}</span>
                </div>
                <div className={styles.profileRow}>
                  <span className={styles.profileLabel}>Âge</span>
                  <span>{calculateAge(user.dateOfBirth)} ans</span>
                </div>
                <div className={styles.profileRow}>
                  <span className={styles.profileLabel}>Genre</span>
                  <span>{user.gender || 'Non renseigné'}</span>
                </div>
                <div className={styles.profileRow}>
                  <span className={styles.profileLabel}>Localisation</span>
                  <span>{user.location || 'Non renseigné'}</span>
                </div>
              </div>
              
              <div className={styles.profileSection}>
                <h3>Contact</h3>
                <div className={styles.profileRow}>
                  <span className={styles.profileLabel}>Email</span>
                  <span>{user.email}</span>
                </div>
                <div className={styles.profileRow}>
                  <span className={styles.profileLabel}>Téléphone</span>
                  <span>{user.phone || 'Non renseigné'}</span>
                </div>
                <div className={styles.profileRow}>
                  <span className={styles.profileLabel}>Préférence de langue</span>
                  <span>{user.preferredLanguage || 'Français'}</span>
                </div>
                <div className={styles.profileRow}>
                  <span className={styles.profileLabel}>Marketing</span>
                  <span>{user.allowMarketing ? 'Accepté' : 'Refusé'}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Section académique */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <IconSchool size={24} />
                Informations académiques
              </h2>
            </div>
            
            <div className={styles.profileInfo}>
              <div className={styles.profileSection}>
                <h3>Scolarité</h3>
                <div className={styles.profileRow}>
                  <span className={styles.profileLabel}>Université</span>
                  <span>{user.university || 'Non renseigné'}</span>
                </div>
                <div className={styles.profileRow}>
                  <span className={styles.profileLabel}>Domaine d'études</span>
                  <span>{user.fieldOfStudy || 'Non renseigné'}</span>
                </div>
                <div className={styles.profileRow}>
                  <span className={styles.profileLabel}>Année de diplôme</span>
                  <span>{user.graduationYear || 'Non renseigné'}</span>
                </div>
              </div>
              
              <div className={styles.profileSection}>
                <h3>Intérêts</h3>
                <div className={styles.profileRow}>
                  <span className={styles.profileLabel}>Centres d'intérêt</span>
                  <div className={styles.interestTags}>
                    {user.interests && user.interests.length > 0 ? 
                      user.interests.map((interest, index) => (
                        <span key={index} className={styles.interestTag}>{interest}</span>
                      )) : 
                      <span>Aucun intérêt spécifié</span>
                    }
                  </div>
                </div>
                <div className={styles.profileRow}>
                  <span className={styles.profileLabel}>Bio</span>
                  <p className={styles.profileBio}>{user.bio || 'Aucune biographie renseignée.'}</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Section statistiques */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <IconClock size={24} />
                Statistiques d'activité
              </h2>
            </div>
            
            <div className={styles.profileInfo}>
              <div className={styles.profileSection}>
                <h3>Engagement</h3>
                <div className={styles.profileRow}>
                  <div className={styles.statBadge}>
                    <div className={styles.statIcon}>
                      <IconCalendar size={18} />
                    </div>
                    <div>
                      <div className={styles.statValue}>{formatDate(user.createdAt)}</div>
                      <div className={styles.statLabel}>Date d'inscription</div>
                    </div>
                  </div>
                </div>
                <div className={styles.profileRow}>
                  <div className={styles.statBadge}>
                    <div className={styles.statIcon}>
                      <IconCalendarEvent size={18} />
                    </div>
                    <div>
                      <div className={styles.statValue}>{formatDate(user.lastLoginAt)}</div>
                      <div className={styles.statLabel}>Dernière connexion</div>
                    </div>
                  </div>
                </div>
                {/* Le champ compteur de connexions a été retiré */}
              </div>
              
              <div className={styles.profileSection}>
                <h3>Activité</h3>
                <div className={styles.profileRow}>
                  <div className={styles.statBadge}>
                    <div className={styles.statIcon}>
                      <IconClock size={18} />
                    </div>
                    <div>
                      <div className={styles.statValue}>{user.totalTimeSpent ? `${user.totalTimeSpent} min` : '0 min'}</div>
                      <div className={styles.statLabel}>Temps total passé</div>
                    </div>
                  </div>
                </div>
                <div className={styles.profileRow}>
                  <div className={styles.statBadge}>
                    <div className={styles.statIcon}>
                      <IconHeartHandshake size={18} />
                    </div>
                    <div>
                      <div className={styles.statValue}>{user.eventsAttendedCount || 0}</div>
                      <div className={styles.statLabel}>Événements assistés</div>
                    </div>
                  </div>
                </div>
                {/* N'afficher "Événements créés" que pour les rôles non-utilisateurs */}
                {user.role !== 'user' && (
                  <div className={styles.profileRow}>
                    <div className={styles.statBadge}>
                      <div className={styles.statIcon}>
                        <IconCrown size={18} />
                      </div>
                      <div>
                        <div className={styles.statValue}>{user.eventsCreatedCount || 0}</div>
                        <div className={styles.statLabel}>Événements créés</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className={styles.emptyState}>
          <p>Aucune information utilisateur disponible.</p>
        </div>
      )}
    </div>
  );
};

export default PersonalInfoPage;
